import { createContext, ReactNode, useContext, useMemo } from 'react';
import { UseQueryState } from 'urql';
import useOfflineQuery from 'hooks/useOfflineQuery';
import {
  Dashboard,
  DashboardDataType,
  InventoriesDashboard,
  PricingDashboard,
  ReportsScreenPdfQuery,
  useReportsScreenPdfQuery,
} from 'generated/graphql';
import { findDashboardTabData } from './reports.utils';
import { useIsReportsPdfMode } from './useIsReportsPdfMode';
import { useReportsDashboard } from './useReportsDashboard';

export interface ReportsScreenPDFContextProps {
  isReportsPdfMode: boolean;
  reportsScreenPdfResult: UseQueryState<ReportsScreenPdfQuery>;
  reportsScreenPdfFetching: boolean;
  refreshReportsScreenPdf: () => void;
  pricingDashboard: PricingDashboard | undefined;
  inventoriesDashboard: InventoriesDashboard | undefined;
}

const Context = createContext<ReportsScreenPDFContextProps | undefined>(undefined);

export const ReportsScreenPDFProvider = ({ children }: { children: ReactNode }) => {
  const { reportsDashboardNewQueryInput } = useReportsDashboard();

  const isReportsPdfMode = useIsReportsPdfMode();

  const [reportsScreenPdfResult, refreshReportsScreenPdf] = useOfflineQuery(
    'reports-screen-pdf-query',
    useReportsScreenPdfQuery,
    {
      variables: {
        input: {
          ...reportsDashboardNewQueryInput,
          printerFriendly: true,
        },
      },
      requestPolicy: 'network-only',
      pause: !isReportsPdfMode || reportsDashboardNewQueryInput.profileID === '0',
    },
  );

  const reportsScreenPdfFetching = (reportsScreenPdfResult.fetching || reportsScreenPdfResult.stale) ?? false;

  const dashboards = reportsScreenPdfResult.data?.reportDashboardsScreen.dashboards as Dashboard[];

  const pricingDashboard = useMemo(
    () => findDashboardTabData<PricingDashboard>(dashboards, DashboardDataType.PricingDashboard),
    [dashboards],
  );

  const inventoriesDashboard = useMemo(
    () => findDashboardTabData<InventoriesDashboard>(dashboards, DashboardDataType.InventoriesDashboard),
    [dashboards],
  );

  const value = useMemo<ReportsScreenPDFContextProps>(
    () => ({
      isReportsPdfMode,
      reportsScreenPdfResult,
      reportsScreenPdfFetching,
      refreshReportsScreenPdf,
      pricingDashboard,
      inventoriesDashboard,
    }),
    [
      isReportsPdfMode,
      reportsScreenPdfResult,
      reportsScreenPdfFetching,
      refreshReportsScreenPdf,
      pricingDashboard,
      inventoriesDashboard,
    ],
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const useReportsScreenPDF = () => {
  const result = useContext(Context);
  if (!result) {
    throw new Error('useReportsScreenPDF must be used within a ReportsScreenPDFProvider');
  }
  return result;
};
